import React, { useMemo, useRef, useState } from 'react';
import { Camera, Table2, LineChart, EyeOff } from 'lucide-react';
import { normalizeDx } from '../utils/deriveSession';

// Validated categorical palette (fixed slot order, never cycled).
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const INK = '#111827';
const MUTED = '#898781';
const GRID = '#e1e0d9';
const MAX_SERIES = 8;

// Layout constants (px), the chart scrolls horizontally in its own container.
const PAD_LEFT = 40;
const PAD_TOP = 34;
const PAD_BOTTOM = 16;
const COL_GAP = 150;
const ROW_GAP = 46;
const LABEL_GUTTER = 170;

const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * Bump chart of how the group's diagnosis ranking evolved across snapshots
 * (auto-taken at each case-stage reveal) plus the live "Now" column.
 * The debrief artifact: "we anchored on X until the labs came back."
 */
export default function Timeline({ snapshots, currentRanking, probabilityMode, revealed = true }) {
  const [mode, setMode] = useState('chart'); // 'chart' | 'table'
  const [hoverCol, setHoverCol] = useState(null);
  const scrollRef = useRef(null);

  const model = useMemo(() => {
    const columns = [
      ...snapshots.map((s) => ({ id: s.id, label: s.label || 'Snapshot', at: s.createdAt, ranking: s.ranking })),
      { id: '__now__', label: 'Now', at: null, ranking: currentRanking },
    ];
    // Series = every diagnosis appearing in any column, keyed by normalized text.
    const seriesMap = new Map();
    columns.forEach((col, ci) => {
      (col.ranking || []).forEach((entry, rank) => {
        const key = normalizeDx(entry.text);
        if (!key) return;
        if (!seriesMap.has(key)) {
          seriesMap.set(key, { key, text: entry.text, points: new Map(), bestRank: Infinity, firstCol: ci, firstRank: rank });
        }
        const s = seriesMap.get(key);
        s.points.set(ci, { rank: rank + 1, score: entry.score });
        s.bestRank = Math.min(s.bestRank, rank + 1);
      });
    });
    const allSeries = [...seriesMap.values()].sort(
      (a, b) => a.firstCol - b.firstCol || a.firstRank - b.firstRank
    );
    const hiddenCount = Math.max(0, allSeries.length - MAX_SERIES);
    let series = allSeries;
    if (hiddenCount) {
      // Keep the most prominent lines; identity color follows the entity.
      series = [...allSeries].sort((a, b) => a.bestRank - b.bestRank || a.firstCol - b.firstCol).slice(0, MAX_SERIES)
        .sort((a, b) => a.firstCol - b.firstCol || a.firstRank - b.firstRank);
    }
    series.forEach((s, i) => { s.color = SERIES_COLORS[i]; });
    const maxRank = Math.max(1, ...columns.map((c) => (c.ranking || []).length));
    return { columns, series, allSeries, maxRank, hiddenCount };
  }, [snapshots, currentRanking]);

  const { columns, series, maxRank, hiddenCount } = model;

  if (!revealed) {
    return (
      <div className="bg-surface border border-line rounded-card p-10 text-center text-ink-2">
        <EyeOff className="w-8 h-8 mx-auto mb-3 text-ink-2/50" />
        <p className="text-body font-medium">Timeline hidden during blind voting</p>
        <p className="text-meta text-ink-2 mt-1">The ranking history will appear when the facilitator reveals the votes.</p>
      </div>
    );
  }

  if (!snapshots.length) {
    return (
      <div className="bg-surface border border-line rounded-card p-10 text-center text-ink-2">
        <Camera className="w-8 h-8 mx-auto mb-3 text-ink-2/50" />
        <p className="text-body font-medium">No snapshots yet</p>
        <p className="text-meta text-ink-2 mt-1 max-w-sm mx-auto">
          Snapshots capture the ranking at a moment in time. They're taken automatically each time the
          facilitator reveals a case stage, or manually via Facilitator → Take snapshot. The timeline then
          shows how the group's thinking changed as information arrived.
        </p>
      </div>
    );
  }

  const width = PAD_LEFT + (columns.length - 1) * COL_GAP + LABEL_GUTTER;
  const height = PAD_TOP + maxRank * ROW_GAP + PAD_BOTTOM;
  const colX = (ci) => PAD_LEFT + ci * COL_GAP;
  const rankY = (rank) => PAD_TOP + (rank - 0.5) * ROW_GAP;

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left + (scrollRef.current?.scrollLeft || 0);
    const ci = Math.round((x - PAD_LEFT) / COL_GAP);
    setHoverCol(ci >= 0 && ci < columns.length ? ci : null);
  };

  const scoreLabel = (score) => (probabilityMode ? `${score}%` : `${score > 0 ? '+' : ''}${score}`);

  return (
    <div className="bg-surface border border-line rounded-card overflow-hidden">
      <div className="flex items-center justify-between px-4 sm:px-6 pt-4">
        <h2 className="text-body font-medium text-ink">
          Ranking over time
          <span className="ml-2 text-meta text-ink-2 font-normal">
            {columns.length - 1} snapshot{columns.length === 2 ? '' : 's'} + now
            {probabilityMode ? ' · median probability' : ' · votes'}
          </span>
        </h2>
        <div className="bg-page rounded-ctl p-0.5 flex" role="tablist" aria-label="Timeline display mode">
          <button
            onClick={() => setMode('chart')}
            className={`px-2.5 py-1 rounded-ctl text-meta flex items-center gap-1 transition ${mode === 'chart' ? 'bg-surface border border-line text-ink' : 'text-ink-2'}`}
            role="tab"
            aria-selected={mode === 'chart'}
          >
            <LineChart className="w-3 h-3" />Chart
          </button>
          <button
            onClick={() => setMode('table')}
            className={`px-2.5 py-1 rounded-ctl text-meta flex items-center gap-1 transition ${mode === 'table' ? 'bg-surface border border-line text-ink' : 'text-ink-2'}`}
            role="tab"
            aria-selected={mode === 'table'}
          >
            <Table2 className="w-3 h-3" />Table
          </button>
        </div>
      </div>

      {mode === 'chart' ? (
        <>
          <div ref={scrollRef} className="overflow-x-auto relative" onMouseLeave={() => setHoverCol(null)}>
            <svg
              width={width}
              height={height}
              onMouseMove={handleMove}
              role="img"
              aria-label="Bump chart of diagnosis rankings across snapshots"
              className="block"
            >
              {/* Recessive horizontal gridlines + rank axis */}
              {Array.from({ length: maxRank }, (_, i) => i + 1).map((rank) => (
                <g key={rank}>
                  <line x1={PAD_LEFT - 8} x2={width - LABEL_GUTTER + 30} y1={rankY(rank)} y2={rankY(rank)} stroke={GRID} strokeWidth="1" />
                  <text x={PAD_LEFT - 14} y={rankY(rank) + 4} textAnchor="end" fontSize="11" fill={MUTED}>{rank}</text>
                </g>
              ))}
              {/* Hover column band */}
              {hoverCol !== null && (
                <rect x={colX(hoverCol) - COL_GAP / 2 + 10} y={PAD_TOP - 10} width={COL_GAP - 20} height={maxRank * ROW_GAP + 10} fill="#6366f1" opacity="0.06" rx="8" />
              )}
              {/* Column labels */}
              {columns.map((col, ci) => (
                <text
                  key={col.id}
                  x={colX(ci)}
                  y={18}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight={ci === columns.length - 1 ? 600 : 400}
                  fill={ci === columns.length - 1 ? INK : MUTED}
                >
                  {truncate(col.label, 18)}
                  <title>{col.label}{col.at ? `, ${new Date(col.at).toLocaleTimeString()}` : ''}</title>
                </text>
              ))}
              {/* Series lines + markers (2px lines; 10px markers with surface ring) */}
              {series.map((s) => {
                const pts = columns.map((_, ci) => s.points.get(ci)).map((p, ci) => (p ? { ci, ...p } : null));
                const segments = [];
                let run = [];
                pts.forEach((p) => {
                  if (p) run.push(p);
                  else if (run.length) { segments.push(run); run = []; }
                });
                if (run.length) segments.push(run);
                const last = [...pts].reverse().find(Boolean);
                const labelled = last && last.ci === columns.length - 1;
                return (
                  <g key={s.key}>
                    {segments.map((seg, i) => (
                      <polyline
                        key={i}
                        points={seg.map((p) => `${colX(p.ci)},${rankY(p.rank)}`).join(' ')}
                        fill="none"
                        stroke={s.color}
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                    ))}
                    {pts.filter(Boolean).map((p) => (
                      <circle key={p.ci} cx={colX(p.ci)} cy={rankY(p.rank)} r="5" fill={s.color} stroke="#ffffff" strokeWidth="2">
                        <title>{`${s.text}, ${columns[p.ci].label}: rank ${p.rank} (${scoreLabel(p.score)})`}</title>
                      </circle>
                    ))}
                    {/* Direct label at the line's end, in ink (relief for low-contrast hues) */}
                    {labelled && (
                      <text x={colX(last.ci) + 12} y={rankY(last.rank) + 4} fontSize="12" fill={INK}>
                        {truncate(s.text, 22)}
                        <title>{s.text}</title>
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
            {/* Tooltip for the hovered column */}
            {hoverCol !== null && columns[hoverCol]?.ranking?.length > 0 && (
              <div
                className="absolute top-10 bg-surface border border-line shadow-pop rounded-card px-3.5 py-2.5 text-meta pointer-events-none z-10 min-w-[180px]"
                style={{
                  left: Math.min(colX(hoverCol) + 16 - (scrollRef.current?.scrollLeft || 0), (scrollRef.current?.clientWidth || width) - 210),
                }}
              >
                <p className="font-semibold text-ink mb-1.5">{columns[hoverCol].label}</p>
                {columns[hoverCol].ranking.slice(0, 8).map((entry, i) => (
                  <p key={i} className="text-ink-2 flex justify-between gap-3">
                    <span className="truncate">{i + 1}. {entry.text}</span>
                    <span className="text-ink-2 flex-shrink-0">{scoreLabel(entry.score)}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
          {/* Legend: chip carries the identity color; text stays in ink */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-4 sm:px-6 pb-4 pt-1">
            {series.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-meta text-ink">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                {truncate(s.text, 28)}
              </span>
            ))}
            {hiddenCount > 0 && <span className="text-meta text-ink-2">+{hiddenCount} more (see table)</span>}
          </div>
        </>
      ) : (
        <div className="overflow-x-auto px-4 sm:px-6 pb-4 pt-2">
          <table className="w-full text-meta min-w-[500px]">
            <thead>
              <tr className="text-left text-ink-2 border-b">
                <th className="py-2 pr-3 font-medium">Diagnosis</th>
                {columns.map((c) => (
                  <th key={c.id} className="py-2 px-2 font-medium text-center" title={c.label}>{truncate(c.label, 14)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...model.allSeries].sort((a, b) => a.bestRank - b.bestRank).map((s) => (
                <tr key={s.key} className="border-b border-line">
                  <td className="py-1.5 pr-3 text-ink flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || '#c3c2b7' }} />
                    {s.text}
                  </td>
                  {columns.map((_, ci) => {
                    const p = s.points.get(ci);
                    return (
                      <td key={ci} className="py-1.5 px-2 text-center text-ink-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {p ? `#${p.rank}` : ', '}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
